import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { Medal } from "@/components/StreakBadges";
import { api } from "@/lib/apiClient";

const DAYS = { "Rising Star": 7, "Moon Cycle": 30, Celestial: 100 };

function Sparkles() {
  const dots = useMemo(
    () => Array.from({ length: 28 }, (_, i) => ({
      left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
      size: 2 + Math.random() * 4, delay: `${(i % 14) * 0.18}s`, dur: `${2.2 + Math.random() * 1.8}s`,
    })), []);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {dots.map((d, i) => (
        <span key={i} className="badge-sparkle" style={{ left: d.left, top: d.top, width: d.size, height: d.size, animationDelay: d.delay, animationDuration: d.dur }} />
      ))}
    </div>
  );
}

export default function BadgeCelebration({ badge, onClose }) {
  const days = DAYS[badge] || 7;
  const [code, setCode] = useState(null);
  useEffect(() => {
    api.get("/streak").then(({ data }) => setCode(data.code)).catch(() => {});
  }, []);

  const share = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/badge/${code}?d=${days}`;
    const text = `I earned the ${badge} badge on Astra: ${days} days in a row with the stars.`;
    try {
      if (navigator.share) await navigator.share({ title: "My Astra streak", text, url });
      else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Badge link copied"); }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share right now");
    }
  };

  return createPortal(
    <div data-testid="badge-celebration" role="dialog" aria-label={`New badge: ${badge}`}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-8 text-center bg-[#04060B]/95 backdrop-blur-xl badge-celebrate-in">
      <Sparkles />
      <p className="text-[11px] uppercase tracking-[0.35em] gold-text mb-8">New badge unlocked</p>
      <div className="badge-medal-pop rounded-full">
        <Medal days={days} earned size={190} />
      </div>
      <h2 data-testid="badge-celebration-title" className="text-4xl font-light mt-8">{badge}</h2>
      <p className="text-[#A7B0C0] mt-3 max-w-xs">{days} days in a row with the stars. Keep the streak glowing.</p>
      <div className="w-full max-w-xs mt-10 space-y-3 relative">
        {code && (
          <button data-testid="btn-celebration-share" onClick={share}
            className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors">
            <Share2 size={17} /> Share my badge
          </button>
        )}
        <button data-testid="btn-celebration-close" onClick={onClose} className="w-full border border-[#D4AF37]/40 gold-text py-3 rounded-full hover:border-[#D4AF37] transition-colors">
          Continue to my reading
        </button>
      </div>
    </div>,
    document.body
  );
}
